/**
 * WinterMarkdown — block-level markdown renderer for Winter's replies.
 *
 * InlineMarkdown (the older sibling) only handles inline tokens, which
 * was fine when Winter answered in two sentences. Now that he pulls
 * data (leaderboards, pipeline tables, transcript quotes) his replies
 * regularly contain lists, tables, and code — this renders them
 * properly instead of leaning on pre-wrap whitespace.
 *
 * Supported (the shapes Claude actually emits — deliberately not a
 * full CommonMark implementation):
 *   ```fenced code blocks```
 *   # / ## / ### headings
 *   - bullet lists (with 2-space nesting flattened to one level)
 *   1. numbered lists
 *   | md | tables | (with header row + separator)
 *   > blockquotes
 *   --- horizontal rules
 *   **bold** *italic* `code` [link](url) inline, in any block
 *
 * Same safety model as InlineMarkdown: every piece of source text is
 * HTML-escaped BEFORE token substitution, output goes into a scoped
 * dangerouslySetInnerHTML. Only http(s) link hrefs survive.
 */

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Inline pass — operates on ALREADY-ESCAPED text.
function renderInline(escaped) {
  let out = escaped;
  // Links first so their label text can still take bold/italic.
  out = out.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (m, label, url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });
  out = out.replace(/`([^`]+)`/g, '<code>$1</code>');
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
  return out;
}

function renderTable(lines) {
  const rows = lines
    .map((l) => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim()));
  if (rows.length < 2) return null;
  // Row 2 must be the ---|--- separator.
  const isSep = rows[1].every((c) => /^:?-{2,}:?$/.test(c));
  if (!isSep) return null;
  const header = rows[0];
  const body = rows.slice(2);
  const th = header.map((c) => `<th>${renderInline(escapeHtml(c))}</th>`).join('');
  const trs = body
    .map((r) => `<tr>${r.map((c) => `<td>${renderInline(escapeHtml(c))}</td>`).join('')}</tr>`)
    .join('');
  return `<table><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table>`;
}

function markdownToHtml(text) {
  const lines = String(text || '').split('\n');
  const html = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (/^```/.test(line.trim())) {
      const buf = [];
      i += 1;
      while (i < lines.length && !/^```/.test(lines[i].trim())) {
        buf.push(lines[i]);
        i += 1;
      }
      i += 1; // consume closing fence (or EOF)
      html.push(`<pre><code>${escapeHtml(buf.join('\n'))}</code></pre>`);
      continue;
    }

    // Table (needs current line + separator line to qualify)
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|[\s\-:|]+\|\s*$/.test(lines[i + 1])) {
      const buf = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        buf.push(lines[i]);
        i += 1;
      }
      const table = renderTable(buf);
      if (table) {
        html.push(table);
        continue;
      }
      // Not a real table — fall through by treating buffered lines as text.
      buf.forEach((l) => html.push(`<p>${renderInline(escapeHtml(l))}</p>`));
      continue;
    }

    // Heading
    const h = line.match(/^(#{1,3})\s+(.*)$/);
    if (h) {
      const level = h[1].length; // 1-3 → all render compact in chat
      html.push(`<div class="wm-h wm-h${level}">${renderInline(escapeHtml(h[2]))}</div>`);
      i += 1;
      continue;
    }

    // Horizontal rule
    if (/^\s*(---+|\*\*\*+)\s*$/.test(line)) {
      html.push('<hr />');
      i += 1;
      continue;
    }

    // Blockquote
    if (/^\s*>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^\s*>\s?/, ''));
        i += 1;
      }
      html.push(`<blockquote>${renderInline(escapeHtml(buf.join(' ')))}</blockquote>`);
      continue;
    }

    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i += 1;
      }
      html.push(`<ul>${items.map((it) => `<li>${renderInline(escapeHtml(it))}</li>`).join('')}</ul>`);
      continue;
    }

    // Ordered list
    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+[.)]\s+/, ''));
        i += 1;
      }
      html.push(`<ol>${items.map((it) => `<li>${renderInline(escapeHtml(it))}</li>`).join('')}</ol>`);
      continue;
    }

    // Blank line — paragraph break (handled implicitly)
    if (!line.trim()) {
      i += 1;
      continue;
    }

    // Paragraph — gather consecutive plain lines, join with <br/> so
    // Claude's soft line breaks survive.
    const buf = [line];
    i += 1;
    while (
      i < lines.length
      && lines[i].trim()
      && !/^```|^#{1,3}\s|^\s*[-*]\s+|^\s*\d+[.)]\s+|^\s*>|^\s*\|.*\|\s*$|^\s*---+\s*$/.test(lines[i])
    ) {
      buf.push(lines[i]);
      i += 1;
    }
    html.push(`<p>${buf.map((l) => renderInline(escapeHtml(l))).join('<br/>')}</p>`);
  }

  return html.join('');
}

export default function WinterMarkdown({ text, style, className }) {
  return (
    <div
      className={`winter-md${className ? ` ${className}` : ''}`}
      style={style}
      // Safe: all source text is escaped before token substitution;
      // only whitelisted tags are emitted and hrefs are http(s)-only.
      dangerouslySetInnerHTML={{ __html: markdownToHtml(text) }}
    />
  );
}
